/**
 * Agent Service
 * Handles communication with the agent integration API endpoints
 */

import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import type {
  AgentImportSession,
  AgentSessionStatus,
  AgentProcessedDataResponse,
} from '@/types/agent.types';

class AgentService {
  private readonly baseUrl = '/api/agent';

  /**
   * Get the status of an agent processing session
   */
  async getSessionStatus(sessionId: string): Promise<AgentImportSession> {
    const apiUrl = getApiUrl();
    const response = await authFetch(
      `${apiUrl}${this.baseUrl}/session/${sessionId}/status`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ?? `Failed to get session status: ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * Get processed data from a completed agent session
   */
  async getProcessedData(sessionId: string): Promise<AgentProcessedDataResponse> {
    const apiUrl = getApiUrl();
    const response = await authFetch(
      `${apiUrl}${this.baseUrl}/session/${sessionId}/processed`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ?? `Failed to get processed data: ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * Cancel an agent processing session
   */
  async cancelSession(sessionId: string): Promise<void> {
    const apiUrl = getApiUrl();
    const response = await authFetch(
      `${apiUrl}${this.baseUrl}/session/${sessionId}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ?? `Failed to cancel session: ${response.status}`
      );
    }
  }

  /**
   * Poll session status until completion or timeout
   * @param sessionId The session ID to poll
   * @param onStatusChange Callback when status changes
   * @param pollIntervalMs Polling interval in milliseconds (default: 2000)
   * @param maxAttempts Maximum number of polling attempts (default: 150 = 5 minutes at 2s intervals)
   * @returns The final session status
   */
  async pollSessionUntilComplete(
    sessionId: string,
    onStatusChange?: (session: AgentImportSession) => void,
    pollIntervalMs: number = 2000,
    maxAttempts: number = 150
  ): Promise<AgentImportSession> {
    let attempts = 0;
    let lastStatus: AgentSessionStatus | null = null;

    while (attempts < maxAttempts) {
      const session = await this.getSessionStatus(sessionId);

      if (session.status !== lastStatus) {
        lastStatus = session.status;
        onStatusChange?.(session);
      }

      if (this.isTerminalStatus(session.status)) {
        return session;
      }

      attempts++;
      await this.delay(pollIntervalMs);
    }

    throw new Error('Agent processing timed out');
  }

  /**
   * Check if a status is terminal (no more processing will occur)
   */
  private isTerminalStatus(status: AgentSessionStatus): boolean {
    return (
      status === 'Completed' ||
      status === 'Failed' ||
      status === 'Cancelled' ||
      status === 'TimedOut'
    );
  }

  /**
   * Delay helper for polling
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const agentService = new AgentService();
